import React, { FC, HTMLAttributes, MouseEvent } from 'react'
import { Box, InputGroup, InputLeftElement, Input, Icon, IconButton, InputRightElement } from '@chakra-ui/core'
import { useLazyQuery } from '@apollo/client'
import { searchProductsByNameQuery } from '../../queries/index'
import { IProduct } from '@engyalo/storefront-types/lib/plugins/model'
import {
    IProductsPricedPerCollectionVariables,
    IProductsPricedPerCollection,
    IProductsPricedPerCollectionCollection,
} from '@engyalo/storefront-types/lib/graphql/product'
import { debounce, notEmpty } from '../../lib/utilities'
import { ProductList, ProductListFrom } from '../ProductList'
import { useParams } from 'react-router'

interface IProps extends HTMLAttributes<HTMLDivElement> {
    placeholder?: string
    status?: boolean
    closeAction: (event: MouseEvent<HTMLButtonElement>) => void
}

export const Searchbar: FC<IProps> = ({ placeholder = 'Buscar producto', status, closeAction }: IProps) => {
    const [query, setQuery] = React.useState('')

    const [searchProducts, { data }] = useLazyQuery<
        IProductsPricedPerCollection,
        IProductsPricedPerCollectionVariables
    >(searchProductsByNameQuery)
    const [products, setProducts] = React.useState<IProduct[]>([])

    const delayedQuery = React.useCallback(
        debounce((q) => searchProducts(q), 500),
        []
    )

    const { customer, order } = useParams()
    const searchInput = React.useRef<HTMLInputElement>(null)

    const onChange = (e: React.FormEvent<HTMLInputElement>): void => {
        setQuery(e.currentTarget.value)
        delayedQuery({
            variables: {
                name: customer,
                configId: order,
                product: query,
            },
        })
    }

    React.useEffect(() => {
        if (query === '') {
            setProducts([])
        } else {
            const searchResult: IProduct[] = []
            data?.productsPricedPerCollection?.forEach((collection: IProductsPricedPerCollectionCollection | null) => {
                if (collection && collection.productsPriced) {
                    const arr: IProduct[] = collection.productsPriced
                        .filter(notEmpty)
                        .map((product) => ({ ...product, enabledPrice: !!collection?.enabledPrice }))
                    searchResult.push(...arr)
                }
            })
            setProducts(searchResult)
        }
    }, [data, query])

    React.useEffect(() => {
        if (status && searchInput.current) {
            searchInput.current.focus()
        }
    }, [status])

    return (
        <Box width="100%" zIndex={100} position="relative">
            <InputGroup borderBottom="1px solid" borderColor="primary.500">
                <InputLeftElement h="40px" paddingX="16px">
                    <Icon name="search" size="14px" />
                </InputLeftElement>
                <Input
                    ref={searchInput}
                    border="none"
                    h="40px"
                    paddingLeft="4rem"
                    type="text"
                    value={query}
                    onChange={onChange}
                    placeholder={placeholder}
                />
            </InputGroup>
            <InputRightElement paddingRight="4px">
                <IconButton
                    width="32px"
                    height="40px"
                    background="transparent"
                    borderColor="transparent"
                    aria-label="cerrar"
                    icon="close"
                    onClick={closeAction}
                />
            </InputRightElement>
            <Box position="absolute" width="100%" backgroundColor="white">
                {products.length > 0 && <ProductList products={products} from={ProductListFrom.SEARCH} />}
            </Box>
        </Box>
    )
}
